import {Component, effect, inject, signal} from '@angular/core';
import {Location, NgClass} from "@angular/common";
import {GenericButtonComponent} from "src/app/presentation/common/generic-button/generic-button.component";
import {UserAgent} from "src/app/infrastructure/moovement-api/models/user";
import {ViewportService} from "src/app/infrastructure/viewport.service";
import {DataStorageService} from "src/app/infrastructure/data-storage.service";
import {environment} from "src/app/environments/environment";
import {appVersion} from "src/app/app-info";
import {LogoutService} from "src/app/infrastructure/logout.service";
import {ToCapitalizedTextPipe} from "src/app/presentation/common/pipes/ToCapitalizedTextPipe";
import {LocationAgeFilterPeriod, MapStore} from 'src/app/domain/map/map.store';
import {FarmStore} from 'src/app/domain/assets/farm/farm.store';
import {FarmService} from 'src/app/domain/assets/farm/farm.service';
import {MainViewStore} from 'src/app/presentation/main-view/main-view.store';
import {Router} from '@angular/router';
import {defaultmaxNativeZoom} from 'src/app/presentation/main-view/map-container/map-container-component';
import {
  NumericStepperComponent
} from "src/app/presentation/main-view/settings/numeric-stepper/numeric-stepper.component";
import {SwitchComponent} from "src/app/presentation/common/switch/switch.component";
import {UserStore} from 'src/app/domain/user/user.store';
import {UserService} from 'src/app/domain/user/user.service';
import {Samples} from 'src/app/infrastructure/moovement-api/models/samples';
import {Share} from "@capacitor/share";
import {Animal, FarmMetadata, SynchronizationStatus} from "src/app/infrastructure/moovement-api/models/asset";
import {ActionType, DataType} from "src/app/domain/domain-models";
import {DataSynchronisationService} from "src/app/infrastructure/data-synchronisation.service";
import {Farm} from "src/app/infrastructure/moovement-api/models/farm";

@Component({
  selector: 'app-settings',
  imports: [
    NgClass,
    GenericButtonComponent,
    ToCapitalizedTextPipe,
    NumericStepperComponent,
    SwitchComponent
  ],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.css'
})
export class SettingsComponent {

  skipAnomalousLocations = signal(false);
  maxZoomLevel = signal(defaultmaxNativeZoom);
  userAgentId = signal<string | undefined>(undefined);
  currentFarm = signal<Farm | undefined>(undefined)
  updatingFarm = signal(false);

  appVersion = appVersion;
  environment = environment;
  locationAgeFilterOptions = [
    LocationAgeFilterPeriod.ALL,
    LocationAgeFilterPeriod.LAST_24_HOURS,
    LocationAgeFilterPeriod.LAST_3_DAYS,
    LocationAgeFilterPeriod.LAST_7_DAYS,
    LocationAgeFilterPeriod.LAST_30_DAYS,
    LocationAgeFilterPeriod.LAST_60_DAYS,
  ];

  AlertStatus = Samples.AlertStatus;
  originalMetadata: FarmMetadata | undefined = undefined
  farmStore = inject(FarmStore);
  location = inject(Location);
  mainViewStore = inject(MainViewStore);
  mapStore = inject(MapStore);
  router = inject(Router);
  userStore = inject(UserStore);
  dataSynchronisationService = inject(DataSynchronisationService);

  private timeoutId?: number;

  constructor(
    private dataStorageService: DataStorageService,
    private farmService: FarmService,
    private logoutService: LogoutService,
    private userService: UserService,
    protected viewportService: ViewportService
  ) {
    effect(() => {
      const farm = this.farmStore.farm();
      if (!farm) return;
      this.currentFarm.set(farm)
      this.skipAnomalousLocations.set(farm.skipAnomalousLocations);
      if (farm.maxZoomLevel) this.maxZoomLevel.set(farm.maxZoomLevel);
    });
    effect(() => {
      if (this.originalMetadata == undefined) {
        this.originalMetadata = this.currentFarm()?.farmMetadata
      }
    })
  }

  async ngOnInit(): Promise<void> {
    const userAgent = await this.dataStorageService.getValueByKey<UserAgent>('userAgent');
    this.userAgentId.set(userAgent?.id);
  }

  logout() {
    this.logoutService.logout()
  }

  routeToDeveloperConsole() {
    this.router.navigate(['/main/developer-console']);
  }

  togglePreferences(key: Samples.AlertStatus) {
    this.userService.togglePreference(key);
  }

  toggleSkipAnomalousLocations() {
    const farm = this.farmStore.farm();
    if (!farm) return;

    this.skipAnomalousLocations.set(!this.skipAnomalousLocations());
    this.scheduleFarmUpdate();
  }

  setMaxZooMLevel(value: number) {
    const farm = this.farmStore.farm();
    if (!farm) return;

    this.maxZoomLevel.set(value);
    this.scheduleFarmUpdate();
  }

  private scheduleFarmUpdate() {
    if (this.timeoutId) window.clearTimeout(this.timeoutId);

    this.timeoutId = window.setTimeout(async () => {
      const farm = this.farmStore.farm();
      if (!farm) return;

      this.updatingFarm.set(true);

      farm.maxZoomLevel = this.maxZoomLevel();
      farm.skipAnomalousLocations = this.skipAnomalousLocations();
      this.farmService.updateFarm(farm).subscribe()
      this.updatingFarm.set(false);
    }, 500);

  }

  selectLocationAgeFilterPeriod(period: LocationAgeFilterPeriod) {
    this.mapStore.setLocationAgeFilterPeriod(period);
  }

  clearLocalDB() {
    if (confirm("This will permanently clear the local database of all farm-related data, a refresh will be required. Continue?")) {
      this.dataStorageService.clearAllAssets()
      this.dataStorageService.clearKeyValuePairs()
      this.dataStorageService.removeKeyValue('lastRefresh')
    }
  }

  protected back(): void {
    this.location.back();
  }

  protected close() {
    this.mainViewStore.hideChildComponent();
  }

  protected async clearDLQ() {
    const dlqEntries = await this.dataStorageService.getDeadLetterEntries()
    for (const entry of dlqEntries) {
      await this.dataStorageService.deleteDeadLetterEntry(entry.id)
    }
  }

  protected async shareDeadLetterQueue() {
    const dlqEntries = await this.dataStorageService.getDeadLetterEntries()
    await Share.share({
      text: JSON.stringify(dlqEntries),
    });
  }

  protected async setMetadata($event: any, type: string) {
    switch (type) {
      case 'eras': this.currentFarm.update(farm => {
        if (!farm) return undefined;
        return {
          ...farm,
          farmMetadata: {...farm.farmMetadata, eras: $event.target.value}
        }
      });
        break;
      case 'sif': this.currentFarm.update(farm => {
        if (!farm) return undefined;
        return {
          ...farm,
          farmMetadata: {...farm.farmMetadata, sif: $event.target.value}
        }
      });
        break;
    }
  }

  async applyChanges() {
    await this.dataSynchronisationService.applyAction(
      DataType.farms,
      ActionType.mergeAsset,
      this.currentFarm()!,
      SynchronizationStatus.syncPending
    );
  }

  protected metadataHasChanged() {
    return this.currentFarm()?.farmMetadata?.sif != this.originalMetadata?.sif || this.currentFarm()?.farmMetadata?.eras != this.originalMetadata?.eras
  }
}
